import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterBar } from '../../../components/filters/FilterBar';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import { STATUS_OPTIONS, type CardFilters } from '../_lib/cardFilters';

/**
 * Filter toolbar for the payout-cards queue (AUT-126) — status multi-select
 * + inspector UUID free-text. Extracted from the page so the page stays
 * under the line cap. Every value is URL-state-driven via the parent's
 * `setFilters` patch.
 *
 * NO search input and NO date range (the backend query schema carries
 * neither). The status multi-select offers the FULL 7-value card-status
 * surface. Inspector is a UUID free-text TextField (the backend's
 * exact-match `inspectorId` filter; a typeahead selector is a follow-up) —
 * mirrors the payouts filter bar.
 */
export interface CardsFilterBarProps {
  filters: CardFilters;
  setFilters: (
    patch: Partial<Record<keyof CardFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const CardsFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: CardsFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <StatusMultiSelect
        status={filters.status ?? []}
        options={STATUS_OPTIONS}
        label={t('cards.filters.statusLabel')}
        onChange={(next) => setFilters({ status: next })}
      />
      <TextField
        size="small"
        label={t('cards.filters.inspectorLabel')}
        placeholder={t('cards.filters.inspectorPlaceholder')}
        value={filters.inspectorId ?? ''}
        onChange={(e) => setFilters({ inspectorId: e.target.value || null })}
        sx={{ minWidth: 200 }}
      />
    </FilterBar>
  );
};
